import { CarImageHolder } from '@components/car-image-holder'
import { AssigneeAvatarPicker } from '@components/assigned-user'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@components/ui/table'
import { VinInput } from '@components/vin'
import { AdasBox, AdasButton, AdasContainer, AdasTypography } from '@components/wrapper-components'
import adasIntelligenceMark from '@components/navigation-bar/navigation-bar/Blue_trimmed_logo.svg'
import { useCallback, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import {
  BUTTONS_DISPLAY_LABEL,
  GENERIC_MESSAGES,
  REDIRECT_URLS,
  getInvoiceSummaryUrl,
  getQuoteSummaryUrl,
  getWorkOrderSummaryUrl
} from '../../constants'
import { StatusEnum, WorkFlowTypesEnum } from '../../enums/enums'
import { currencyFormatter } from '../../utils/utils'
import { useIsReadOnly } from '@hooks/useIsReadOnly'
import { useModuleAccess } from '@hooks/useModuleAccess'

const TYPE_PILL_STYLES = {
  [WorkFlowTypesEnum.QUOTE]: 'bg-[#E8EFFF] text-[#1F6FEB]',
  [WorkFlowTypesEnum.WORKORDER]: 'bg-[#FEF3C7] text-[#B45309]',
  [WorkFlowTypesEnum.INVOICE]: 'bg-[#DCFCE7] text-[#16803F]'
}

const ASSIGNEE_TYPE_MAP = {
  workorder: 'workorders',
  invoice: 'invoices',
  quote: 'quotes'
}

const getType = (item) => {
  if (item.table_name === 'workorder') return WorkFlowTypesEnum.WORKORDER
  if (item.table_name === 'quote') return WorkFlowTypesEnum.QUOTE
  if (item.table_name === 'invoice') return WorkFlowTypesEnum.INVOICE
  return ''
}

const formatCreatedAt = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const date = d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
  return `${date} ${time}`
}

export const VinSearchPage = () => {
  const location = useLocation()
  const { vin: VinNumber } = location?.state || {}
  const [vinSearchList, setVinSearchList] = useState([])
  const [noRecordsFound, setNoRecordsFound] = useState(false)
  const [vin, setVin] = useState(VinNumber || '')
  const history = useHistory()
  const isReadOnly = useIsReadOnly()
  const canUse = useModuleAccess()

  const handleClick = (item) => {
    if (item.table_name === 'invoice') history.push(getInvoiceSummaryUrl(item?.reference_number))
    else if (item.table_name === 'workorder') history.push(getWorkOrderSummaryUrl(item?.reference_number))
    else history.push(getQuoteSummaryUrl(item?.reference_number))
  }

  const redirectTo = (redirectUrl) => () => {
    history.push(redirectUrl, { prepopulateVin: vin })
  }

  const updateVINSearchList = useCallback((fetchedResults) => {
    setVinSearchList([])
    setNoRecordsFound(false)
    if (fetchedResults?.vin) setVin(fetchedResults.vin)
    if (fetchedResults?.vinList?.length > 0) setVinSearchList(fetchedResults.vinList)
    else setNoRecordsFound(true)
  }, [])

  return (
    <AdasContainer maxWidth='xl'>
      <AdasBox className='pt-6 md:pt-4 mb-6'>
        <h1 className='text-[32px] font-bold leading-tight tracking-tight text-foreground'>VIN Search</h1>
      </AdasBox>

      {/* VIN input + helper copy */}
      <AdasBox className='mb-6 rounded-xl border border-[#EBEDF0] bg-white px-6 py-5 shadow-sm'>
        <AdasTypography className='mb-2 text-[13px] font-medium text-[#475569]'>{GENERIC_MESSAGES.SEARCH_ALL}</AdasTypography>
        <VinInput shouldFetchOnlyVinList={true} updateVINSearchList={updateVINSearchList} prepopulateVin={vin} />
        <AdasTypography className='mt-3 text-[12px] text-[#6B7280]'>{GENERIC_MESSAGES.VIN_MUST_MATCH}</AdasTypography>
        {vinSearchList.length === 0 && !noRecordsFound && (
          <AdasBox className='mt-4 flex h-[260px] w-full items-center justify-center rounded-2xl bg-[#F1F3F5]'>
            <CarImageHolder showText={false} />
          </AdasBox>
        )}
      </AdasBox>

      {/* Search results */}
      {vinSearchList.length > 0 && (
        <AdasBox className='mb-6'>
          <AdasTypography className='mb-3 text-[14px] font-semibold text-[#0A1628]'>{GENERIC_MESSAGES.SEARCH_RESULTS}</AdasTypography>

          {/* Mobile card list */}
          <div className='min-[435px]:hidden space-y-3'>
            {vinSearchList.map((item) => {
              const type = getType(item)
              const isOpen = StatusEnum[item.status] === StatusEnum.OPEN
              const isAi = item.source === 'ADAS_INTELLIGENCE' || item.ai_processed
              const vehicleName = [item.vehicle?.year, item.vehicle?.make, item.vehicle?.model].filter(Boolean).join(' ') || '—'
              const typeStyle = TYPE_PILL_STYLES[type] || 'bg-[#EBEDF0] text-[#475569]'
              return (
                <div
                  key={item.id}
                  onClick={() => handleClick(item)}
                  className='cursor-pointer rounded-xl border border-[#E5E7EB] bg-white p-4 shadow-sm transition-colors hover:bg-[#F8FAFC]'
                >
                  <div className='flex items-center justify-between gap-2'>
                    <div className='flex items-center gap-x-1.5 text-[12px] text-[#6B7280]'>
                      <span>
                        {type} #{item.reference_number}
                      </span>
                      {item.repair_order_num && (
                        <>
                          <span className='text-[#CBD5E1]'>|</span>
                          <span>RO #{item.repair_order_num}</span>
                        </>
                      )}
                    </div>
                    <div className='flex items-center gap-2 shrink-0'>
                      {isAi && (
                        <img
                          src={adasIntelligenceMark}
                          alt='ADAS Intelligence'
                          title='Created from ADAS Intelligence'
                          className='h-3 w-auto shrink-0 object-contain'
                        />
                      )}
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold uppercase ${typeStyle}`}>
                        {type}
                      </span>
                    </div>
                  </div>
                  <div className='mt-1 text-[16px] font-bold uppercase leading-tight tracking-tight text-[#0A1628]'>{vehicleName}</div>
                  {item.client?.name && (
                    <div className='mt-0.5 text-[13px] text-[#6B7280]'>
                      <span>{item.client.name}</span>
                      {item.insurance_company?.name && (
                        <>
                          <span className='mx-1.5 text-[#CBD5E1]'>·</span>
                          <span>{item.insurance_company.name}</span>
                        </>
                      )}
                    </div>
                  )}
                  {item.vehicle?.vin && (
                    <div className='mt-1 font-mono text-[11px] tracking-wider text-[#6B7280]'>VIN {item.vehicle.vin}</div>
                  )}
                  <div className='mt-2 flex items-center' onClick={(e) => e.stopPropagation()}>
                    <AssigneeAvatarPicker
                      type={ASSIGNEE_TYPE_MAP[item.table_name]}
                      workOrderId={item.id}
                      initialAssignee={item.assigned_to}
                      closedStatus={StatusEnum[item.status] === StatusEnum.CLOSED}
                    />
                    <span
                      className={`ml-auto inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-bold uppercase ${
                        isOpen ? 'bg-[#E8EFFF] text-[#2C3D4D]' : 'bg-[#EBEDF0] text-[#6B7280]'
                      }`}
                    >
                      <span className={`inline-block h-1.5 w-1.5 rounded-full ${isOpen ? 'bg-[#2C3D4D]' : 'bg-[#6B7280]'}`} />
                      {StatusEnum[item.status] || item.status}
                    </span>
                  </div>
                  <hr className='my-3 border-[#EBEDF0]' />
                  <div className='flex items-end justify-between'>
                    <div className='text-[18px] font-bold text-[#0A1628]'>{currencyFormatter(item.total_cost)}</div>
                    <div className='text-[12px] text-[#6B7280]'>Created {formatCreatedAt(item.created_at)}</div>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Desktop table */}
          <div className='hidden min-[435px]:block overflow-hidden rounded-lg border border-[#D1D5D8] bg-white'>
            <Table>
              <TableHeader>
                <TableRow className='bg-gradient-to-r from-[#2C3D4D] to-[#1F2D3D] hover:from-[#2C3D4D] hover:to-[#1F2D3D]'>
                  <TableHead className='w-[100px] text-white text-[12px] font-semibold uppercase tracking-wide'>Type</TableHead>
                  <TableHead className='text-white text-[12px] font-semibold uppercase tracking-wide'>Reference</TableHead>
                  <TableHead className='text-white text-[12px] font-semibold uppercase tracking-wide'>Client</TableHead>
                  <TableHead className='text-white text-[12px] font-semibold uppercase tracking-wide'>Assignee</TableHead>
                  <TableHead className='text-white text-[12px] font-semibold uppercase tracking-wide'>Status</TableHead>
                  <TableHead className='text-white text-[12px] font-semibold uppercase tracking-wide'>Amount</TableHead>
                  <TableHead className='text-white text-[12px] font-semibold uppercase tracking-wide'>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {vinSearchList.map((item) => {
                  const type = getType(item)
                  const isOpen = StatusEnum[item.status] === StatusEnum.OPEN
                  const typeStyle = TYPE_PILL_STYLES[type] || 'bg-[#EBEDF0] text-[#475569]'
                  const isAi = item.source === 'ADAS_INTELLIGENCE' || item.ai_processed
                  return (
                    <TableRow
                      key={item.id}
                      onClick={() => handleClick(item)}
                      className='cursor-pointer h-[72px] even:bg-[#F8FAFC] hover:bg-[#F0F4FF]'
                    >
                      <TableCell className='align-middle'>
                        <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-bold uppercase ${typeStyle}`}>
                          {type}
                        </span>
                      </TableCell>
                      <TableCell className='align-middle pr-10 whitespace-nowrap'>
                        <div className='min-w-[280px]'>
                          <div className='flex items-center gap-x-1.5 text-[12px] text-[#6B7280] whitespace-nowrap'>
                            <span>
                              {type} #{item.reference_number}
                            </span>
                            {item.repair_order_num && (
                              <>
                                <span className='text-[#CBD5E1]'>|</span>
                                <span>RO #{item.repair_order_num}</span>
                              </>
                            )}
                            {item.insurance_company?.name && (
                              <>
                                <span className='text-[#CBD5E1]'>|</span>
                                <span>{item.insurance_company.name}</span>
                              </>
                            )}
                          </div>
                          <div className='mt-1 flex items-center gap-2 text-[15px] font-bold uppercase tracking-tight text-[#0A1628] whitespace-nowrap'>
                            <span>{[item.vehicle?.year, item.vehicle?.make, item.vehicle?.model].filter(Boolean).join(' ') || '—'}</span>
                            {isAi && (
                              <img
                                src={adasIntelligenceMark}
                                alt='ADAS Intelligence'
                                title='Created from ADAS Intelligence'
                                className='inline-block h-3 w-auto shrink-0 object-contain'
                              />
                            )}
                          </div>
                          {item.vehicle?.vin && (
                            <div className='mt-0.5 font-mono text-[11px] text-[#6B7280] tracking-wider whitespace-nowrap'>
                              {item.vehicle.vin}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className='align-middle text-[13px] text-[#0A1628]'>{item.client?.name || '—'}</TableCell>
                      <TableCell className='align-middle' onClick={(e) => e.stopPropagation()}>
                        <AssigneeAvatarPicker
                          type={ASSIGNEE_TYPE_MAP[item.table_name]}
                          workOrderId={item.id}
                          initialAssignee={item.assigned_to}
                          closedStatus={StatusEnum[item.status] === StatusEnum.CLOSED}
                        />
                      </TableCell>
                      <TableCell className='align-middle'>
                        <span
                          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold uppercase ${
                            isOpen ? 'bg-[#E8EFFF] text-[#2C3D4D]' : 'bg-[#EBEDF0] text-[#6B7280]'
                          }`}
                        >
                          <span className={`inline-block h-1.5 w-1.5 rounded-full ${isOpen ? 'bg-[#2C3D4D]' : 'bg-[#6B7280]'}`} />
                          {StatusEnum[item.status] || item.status}
                        </span>
                      </TableCell>
                      <TableCell className='align-middle text-[15px] font-bold text-[#0A1628]'>
                        {currencyFormatter(item.total_cost)}
                      </TableCell>
                      <TableCell className='align-middle text-[13px] text-[#6B7280]'>{formatCreatedAt(item.created_at)}</TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>
        </AdasBox>
      )}

      {/* No-records empty state */}
      {noRecordsFound && (
        <AdasBox className='mb-6 rounded-xl border border-[#EBEDF0] bg-white px-6 py-8 text-center shadow-sm'>
          <AdasTypography className='text-[16px] font-bold text-[#0A1628]'>{GENERIC_MESSAGES.NO_RECORDS_FOUND}</AdasTypography>
          <AdasTypography className='mt-2 text-[13px] text-[#475569]'>{GENERIC_MESSAGES.TRY_OTHER_VIN}</AdasTypography>
          <AdasTypography className='mt-3 font-mono text-[13px] uppercase tracking-wider text-[#0A1628]'>{vin}</AdasTypography>
          {/* Both only lead to create screens, which a client user cannot open; each shows only when
              the center's plan includes it (work orders and quotes are separate features). */}
          {!isReadOnly && (canUse('work_orders') || canUse('quotes')) && (
            <AdasBox className='mt-5 flex flex-wrap items-center justify-center gap-3'>
              {canUse('work_orders') && (
                <AdasButton
                  variant='contained'
                  onClick={redirectTo(REDIRECT_URLS.NEW_WORK_ORDER)}
                  className='rounded-lg bg-[#0A1628] px-6 font-semibold text-white hover:bg-[#1F2D3D]'
                >
                  {BUTTONS_DISPLAY_LABEL.NEW_WORK_ORDER}
                </AdasButton>
              )}
              {canUse('quotes') && (
                <AdasButton variant='outlined' onClick={redirectTo(REDIRECT_URLS.NEW_QUOTE)} className='rounded-lg px-6 font-semibold'>
                  {BUTTONS_DISPLAY_LABEL.NEW_QUOTE}
                </AdasButton>
              )}
            </AdasBox>
          )}
        </AdasBox>
      )}
    </AdasContainer>
  )
}
